import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
// The context, not the `useExpressApiContext` hook: the hook throws when no
// provider is above it, and this hook is rendered bare in its own tests.
import {
	ExpressApiContext,
	RECONCILE_MIN_INTERVAL_MS,
} from '@/api/ExpressApiContext';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';
import {
	postScheduleIso,
	SCHEDULE_ABANDONED_MS,
	schedulePublishState,
} from '@/spaces/chat/panels/scheduleUtils';

import type {
	Action,
	SendEmailToContactsConfig,
} from '../../../bridge/types/action.ts';
import type {
	Artifact,
	ContentStatus,
	InspirationReference,
} from '../../../bridge/types/artifact.ts';
import { isInspirationReference } from '../../../bridge/types/artifact.ts';
import type { Process } from '../../../bridge/types/process.ts';
import {
	isEmptyPlaintextEmail,
	isRenderableContentArtifact,
	isSkippedInProcess,
	isUngeneratedHtmlEmail,
	isUngeneratedPostInProposedProcess,
	type RenderableContentArtifact,
} from '../../../bridge/utils/ownerWaitingWork.ts';

export type { RenderableContentArtifact };

export type EffectiveStatus = ContentStatus | 'paused';

// Adobe Express publishes about a minute after the publish instant, so a look
// before this is a look at an answer that does not exist yet.
export const RECHECK_SETTLE_MS = 2 * 60 * 1000;

// The earliest a re-check can produce a fresh answer. The app asks the server to
// look again while it starts up, which arms the reconcile throttle, so a wake
// inside that window is swallowed and reads nothing new. The settle window on
// top absorbs the gap between mount and that first call, which happens after two
// awaited requests.
export const RECHECK_FLOOR_MS = RECONCILE_MIN_INTERVAL_MS + RECHECK_SETTLE_MS;

// setTimeout keeps its delay in a signed 32 bit integer, so a larger value wraps
// and fires the callback at once.
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

// One focus read is three Cosmos-backed queries, and a `focus` event is cheap to
// raise: a window manager, a browser dialog and the URL bar all raise it, so one
// visit can deliver several in a second. A returning customer cannot read the
// screen faster than this, so serve such a burst with one read.
//
// This floor is deliberately much shorter than RECONCILE_MIN_INTERVAL_MS. That
// throttle bounds a POST that queues one Adobe Express request per post plus a
// Meta Graph sync, and a floor that long here would hold a stale "Past due" on
// screen for ten minutes, which is the defect this ticket exists to fix. The
// value matches the floor the provider puts on its Express-connection focus
// handler (ExpressApiProvider.tsx:192-193). The provider's other focus handler,
// the reconcile one, carries RECONCILE_MIN_INTERVAL_MS instead.
export const FOCUS_REFRESH_MIN_INTERVAL_MS = 5 * 1000;

// A post that never resolves must not keep this tab working forever. The budget
// is per publish attempt: a customer can hold several posts due hours apart in
// one visit, and a shared budget would spend itself on the first few and leave
// the rest with no trigger at all for the remainder of the session.
export const MAX_RECHECKS = 3;

// The budget belongs to one publish attempt, so its key carries the schedule
// instant as well as the post. A customer who cannot confirm a post is told to
// schedule it again (PostContentDetail.tsx:159-161), and that PATCHes the same
// artifact id (PostContentDetail.tsx:356-359). Keyed on the id alone, the new
// attempt would inherit the spent budget of the old one and get no look at all.
// A stored schedule instant never moves on its own: postScheduleIso reads
// `fire_at` or the stored `on`/`at` pair, so only the customer changes it.
const recheckAttemptKey = (id: string, due: number): string => `${id}@${due}`;

export const getEffectiveStatus = (
	artifact: RenderableContentArtifact,
	processById: Map<string, Process>,
): EffectiveStatus => {
	const parent = artifact.process_id
		? (processById.get(artifact.process_id) ?? null)
		: null;
	if (
		parent?.status === 'paused' &&
		(artifact.status === 'scheduled' || artifact.status === 'processing')
	) {
		return 'paused';
	}
	if (
		parent?.status === 'cancelled' &&
		artifact.status !== 'published' &&
		artifact.status !== 'failed'
	) {
		return 'cancelled';
	}
	return artifact.status;
};

export type UseContentArtifactsResult = {
	content: RenderableContentArtifact[];
	inspirations: InspirationReference[];
	processById: Map<string, Process>;
	emailActionByRef: Map<string, Action>;
	isLoading: boolean;
	refresh: () => Promise<void>;
};

export const useContentArtifacts = (): UseContentArtifactsResult => {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const [rawArtifacts, setRawArtifacts] = useState<Artifact[]>([]);
	const [processes, setProcesses] = useState<Process[]>([]);
	const [actions, setActions] = useState<Action[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const isMountedRef = useRef(true);
	// Counts the reads this mount has issued. Several can be in flight at once: a
	// focus read, the settle read behind it, the re-check timer's read, and a read
	// after a mutation. Each read is three requests joined by Promise.all, so the
	// responses can land in any order and an older read can answer last. Only the
	// newest read issued may apply its result.
	const latestFetchRef = useRef(0);

	useEffect(() => {
		// StrictMode runs effect setup/cleanup twice in dev. Re-assert mounted=true
		// on each setup so async guards don't get stuck false.
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const fetchArtifacts = useCallback(
		async (caller: () => boolean) => {
			const seq = ++latestFetchRef.current;
			// A read that another read has already superseded must not write its
			// answer, or it repaints the older list over the newer one.
			const shouldApply = () => caller() && latestFetchRef.current === seq;

			if (!userId) {
				if (shouldApply()) {
					setRawArtifacts([]);
					setProcesses([]);
					setActions([]);
					setIsLoading(false);
				}
				return;
			}

			try {
				const [artifactsResult, processesResult, actionsResult] =
					await Promise.all([
						bridgewayApi
							.get<Artifact[]>(`/api/user/${userId}/artifacts/`)
							.catch(() => [] as Artifact[]),
						bridgewayApi
							.get<Process[]>(`/api/user/${userId}/processes/`)
							.catch(() => [] as Process[]),
						bridgewayApi
							.get<Action[]>(`/api/user/${userId}/actions/`)
							.catch(() => [] as Action[]),
					]);
				if (shouldApply()) {
					setRawArtifacts(artifactsResult);
					setProcesses(processesResult);
					setActions(actionsResult);
				}
			} catch (error) {
				if (shouldApply()) {
					logger.error('Content artifact load failed', error);
				}
			} finally {
				if (shouldApply()) {
					setIsLoading(false);
				}
			}
		},
		[bridgewayApi, userId],
	);

	const refresh = useCallback(async () => {
		await fetchArtifacts(() => isMountedRef.current);
	}, [fetchArtifacts]);

	useEffect(() => {
		// Content only changes via chat (which persists to Cosmos), so a periodic
		// poll is unnecessary. Fetch once on mount; explicit refresh() after
		// mutations (schedule/unschedule/delete/close-detail) keeps the list fresh.
		let isCancelled = false;
		const shouldApply = () => !isCancelled && isMountedRef.current;
		void fetchArtifacts(shouldApply);

		return () => {
			isCancelled = true;
		};
	}, [fetchArtifacts]);

	const expressContext = useContext(ExpressApiContext);
	const reconcile = expressContext?.reconcileInstagramPosts;
	const lastReconcileAt = expressContext?.lastReconcileAt;
	const hasInstagram = expressContext?.hasValidInstagramConnection ?? false;

	// The focus effect below reads the connection through a ref so the flag stays
	// out of its dependency list. The flag flips whenever `getCalendars` answers
	// empty, and the provider re-reads it on every token refresh. In the list, a
	// flip would run the cleanup and clear a settle read that is already armed,
	// losing the read the 202 behind it needs.
	const hasInstagramRef = useRef(hasInstagram);
	useEffect(() => {
		hasInstagramRef.current = hasInstagram;
	}, [hasInstagram]);

	// A customer who leaves the tab and comes back may have missed a post going
	// live. Bridgeway's own copy of the row is what the screen reads, so refresh
	// it rather than showing a stale schedule.
	const lastFocusReadAtRef = useRef(0);
	useEffect(() => {
		let settleTimer: ReturnType<typeof setTimeout> | undefined;
		const read = () => {
			lastFocusReadAtRef.current = Date.now();
			void refresh();
		};
		const handleFocus = () => {
			// One visit can raise several focus events, and each read costs three
			// Cosmos-backed queries. The floor is seconds, so a customer who comes
			// back still sees a current screen.
			if (
				Date.now() - lastFocusReadAtRef.current >=
				FOCUS_REFRESH_MIN_INTERVAL_MS
			) {
				read();
			}
			// The provider asks the server to look again on this same event, and
			// that route answers 202. So the read above sees the row from before
			// it, and one more read is needed once an answer can exist. This side
			// must not gate that read on the reconcile's own boolean: the
			// provider's listener takes the throttle slot first, so this side
			// would read false and skip the very read that is needed.
			//
			// It does gate on the connection, because that is what decides whether
			// a reconcile runs at all. The provider's own listener returns early on
			// the same flag (ExpressApiProvider.tsx:155), so with no connection no
			// 202 is ever queued and this second read would answer nothing.
			if (!hasInstagramRef.current) return;
			clearTimeout(settleTimer);
			settleTimer = setTimeout(read, RECHECK_SETTLE_MS);
		};
		window.addEventListener('focus', handleFocus);
		return () => {
			clearTimeout(settleTimer);
			window.removeEventListener('focus', handleFocus);
		};
	}, [refresh]);
	// Looks already spent per publish attempt, keyed by recheckAttemptKey, for the
	// life of the mount. One reconcile asks about every scheduled post, so this
	// counts the attempt the wake was armed for.
	const recheckCountByAttemptRef = useRef(new Map<string, number>());

	// The earliest instant at which a reconcile THIS MOUNT asked for can produce a
	// fresh answer. It is an instant, not a delay, because this effect re-arms on
	// every read: a delay measured from the new `now` restarts the whole wait. A
	// customer who clicks in and out of a post, or schedules one, calls refresh()
	// (ContentSpace.tsx:112, PostContentDetail.tsx:489), and doing that more often
	// than RECHECK_FLOOR_MS would otherwise push the wake out for ever.
	//
	// It starts at zero. Seeded with the mount instant plus RECHECK_FLOOR_MS, it
	// held every wake until 12 minutes after the customer opened the Content
	// space. Leaving the space unmounts this hook (Spaces.tsx:73-79), so a
	// customer moving between Chat and Content lost the look on every visit
	// shorter than the floor. The throttle the floor exists to clear belongs to
	// the provider, which outlives a space change, so the effect reads it there.
	const nextRecheckAtRef = useRef(0);

	// A customer who watches a post publish never leaves the tab, so no focus
	// event ever arrives and nothing else asks the server to look again. This
	// timer covers them: shortly after the earliest scheduled post is due, ask
	// the server for a fresh answer, then read the result.
	//
	// It is a bounded re-check, not a poll. MAX_RECHECKS caps the looks per post,
	// and the reconcile call has its own floor on top of that, so the rate the
	// server sees is bounded however many posts a customer holds.
	useEffect(() => {
		const spentByAttempt = recheckCountByAttemptRef.current;
		const now = Date.now();
		// A post this late is past every plausible publish window, so the clock
		// alone settles what the screen says about it. Such posts also arrive in
		// backlogs, and one would otherwise always hold the earliest instant and
		// take the wake from a post that is genuinely about to publish. The app
		// start and focus reconciles still cover them.
		const settledBefore = now - SCHEDULE_ABANDONED_MS;

		const waiting: { key: string; due: number }[] = [];
		let earliestDue = Number.POSITIVE_INFINITY;
		for (const artifact of rawArtifacts) {
			if (artifact.type !== 'instagram_post') continue;
			if (artifact.status !== 'scheduled') continue;
			const iso = postScheduleIso(artifact);
			if (!iso) continue;
			const due = new Date(iso).getTime();
			if (Number.isNaN(due)) continue;
			if (due < settledBefore) continue;
			// The badge already has its answer for this post — the server counted
			// its 404s and gave up, or the clock alone settled it. Waking for it
			// again cannot change that verdict, and would only spend one of its
			// three looks for nothing.
			if (schedulePublishState(iso, artifact.express_metadata) === 'past_due')
				continue;
			// This publish attempt has had its looks. Skipping it rather than
			// returning leaves the wake for the next post that can still use one.
			const key = recheckAttemptKey(artifact.id, due);
			if ((spentByAttempt.get(key) ?? 0) >= MAX_RECHECKS) continue;
			waiting.push({ key, due });
			if (due < earliestDue) earliestDue = due;
		}
		if (waiting.length === 0) return;

		// The floor keeps the wake outside the reconcile's own throttle window, so
		// the call that follows it can run. One reconcile asks about every scheduled
		// post, so a single wake covers each post that comes due inside the floor.
		//
		// The throttle sits on the provider, which outlives this hook. Zero means no
		// reconcile has run yet, so nothing has to be waited out.
		const reconciledAt = lastReconcileAt?.() ?? 0;
		const sharedFloor =
			reconciledAt === 0 ? 0 : reconciledAt + RECHECK_FLOOR_MS;
		const wakeAt = Math.max(
			earliestDue + RECHECK_SETTLE_MS,
			nextRecheckAtRef.current,
			sharedFloor,
		);
		const delay = Math.max(wakeAt - now, 0);
		// A wake this far out cannot be timed. The customer is not going to hold the
		// tab open until then, and the effect re-arms on every fetch.
		if (delay > MAX_TIMER_DELAY_MS) return;

		const timers: ReturnType<typeof setTimeout>[] = [];
		// attempt() is async and can still be awaiting `reconcile()` when this
		// effect's cleanup runs (rawArtifacts changed, or the hook unmounted). A
		// timer pushed to `timers` after that point would never be cleared by the
		// cleanup below, since cleanup only runs once. This flag stops such a
		// stale resolution from arming anything further.
		let cancelled = false;

		const attempt = async (isRetry: boolean) => {
			// Recorded before the call, whatever the call answers. It stops the
			// re-arm that follows this call's own read from firing a second attempt
			// at once. The provider's own throttle cannot do this job: it arms only
			// on a call that ran (ExpressApiProvider.tsx:73-76).
			nextRecheckAtRef.current = Date.now() + RECHECK_FLOOR_MS;
			const ran = (await reconcile?.()) ?? false;
			if (cancelled) return;
			if (!ran && !isRetry) {
				// The throttle swallowed the call, or the route failed, so there is no
				// new answer to read yet. Do not spend one of the three looks on it.
				// Wait for the throttle to expire and try once more.
				timers.push(
					setTimeout(() => void attempt(true), RECONCILE_MIN_INTERVAL_MS),
				);
				return;
			}
			// A retry that answered false falls through to the same charge and read.
			// A return here ended the chain for the life of the mount. No read means
			// no change to `rawArtifacts`, and this effect re-arms on nothing else.
			// The customer this timer exists for never leaves the tab. No focus event
			// and no space change reaches them either. So their post kept its stale
			// label for the rest of the session, which is this ticket's own symptom.
			// A throttled call also leaves an answer worth reading: some other caller
			// ran a reconcile inside the last RECONCILE_MIN_INTERVAL_MS. The charge
			// keeps the chain bounded. Without it, a route that never answers would
			// re-arm for ever.
			//
			// One reconcile asks the server about every scheduled post, so this call
			// answered for every post that was already due when it ran. Charge each
			// of those. Charging the woken post alone would leave the rest holding a
			// full budget for a look they already had, and a backlog of N posts
			// would then cost MAX_RECHECKS reconciles each instead of in total.
			const answeredAt = Date.now();
			for (const post of waiting) {
				if (post.due > answeredAt) continue;
				spentByAttempt.set(post.key, (spentByAttempt.get(post.key) ?? 0) + 1);
			}
			// The route answers 202 and does its work in the background, so wait
			// before reading Bridgeway's copy of the row.
			timers.push(setTimeout(() => void refresh(), RECHECK_SETTLE_MS));
		};

		timers.push(setTimeout(() => void attempt(false), delay));

		return () => {
			cancelled = true;
			for (const timer of timers) clearTimeout(timer);
		};
	}, [rawArtifacts, refresh, reconcile, lastReconcileAt]);

	const processById = useMemo(
		() => new Map(processes.map((p) => [p.id, p])),
		[processes],
	);

	const emailActionByRef = useMemo(() => {
		const map = new Map<string, Action>();
		const artifactIdByLookupKey = new Map<string, string>();
		for (const artifact of rawArtifacts) {
			artifactIdByLookupKey.set(artifact.id, artifact.id);
			if (artifact.ref) artifactIdByLookupKey.set(artifact.ref, artifact.id);
		}
		for (const action of actions) {
			if (action.type !== 'send_email_to_contacts') continue;
			const ref = (action.config as SendEmailToContactsConfig).artifact_ref;
			if (!ref) continue;
			const artifactId = artifactIdByLookupKey.get(ref);
			if (!artifactId) continue;
			map.set(artifactId, action);
		}
		return map;
	}, [actions, rawArtifacts]);

	const content = useMemo(
		() =>
			rawArtifacts.filter((a): a is RenderableContentArtifact => {
				if (!isRenderableContentArtifact(a)) return false;
				const parent = a.process_id
					? (processById.get(a.process_id) ?? null)
					: null;
				// The grid shows more than waiting work — scheduled, published and
				// paused items belong here too — so it shares only the exclusions with
				// isWaitingOnOwner, not the whole predicate.
				if (isSkippedInProcess(a, parent)) return false;
				if (isUngeneratedPostInProposedProcess(a, parent)) return false;
				if (isUngeneratedHtmlEmail(a)) return false;
				if (isEmptyPlaintextEmail(a)) return false;
				if (a.status !== 'cancelled') return true;
				return parent?.status === 'cancelled';
			}),
		[rawArtifacts, processById],
	);

	// Inspiration references arrive on the same artifacts fetch but are not
	// content the user drafts or schedules, so they get their own list rather
	// than being squeezed through the content grid's filters.
	const inspirations = useMemo(
		() =>
			rawArtifacts
				.filter(isInspirationReference)
				.filter((a) => a.status === 'active')
				.sort((a, b) =>
					(b.content.shared_at || '').localeCompare(a.content.shared_at || ''),
				),
		[rawArtifacts],
	);

	return {
		content,
		inspirations,
		processById,
		emailActionByRef,
		isLoading,
		refresh,
	};
};
